import { useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '~/components/ui/dialog';
import { Button } from '~/components/ui/button';
import { useFamiliesForEnrollmentQuery } from './useFamiliesForEnrollmentQuery';
import { useCreateEnrollments } from './useCreateEnrollments';
import SelectKindStep from './wizardSteps/SelectKindStep';
import SelectFamiliesStep from './wizardSteps/SelectFamiliesStep';
import SelectStudentsStep from './wizardSteps/SelectStudentsStep';
import type { GradeLevelItem, WizardFamilyWithStatus } from './types';

const STEPS = ['kind', 'families', 'students', 'confirm'] as const;

interface StudentSelection {
  personId: string;
  selected: boolean;
  gradeLevel: string;
  enrollmentKind: string | null;
}

/**
 * The grade after `grade` in the school's grade order. A child in the last
 * grade, or in one the school doesn't list, starts on none.
 */
function gradeAfter(grade: string | null | undefined, gradeLevels: GradeLevelItem[]): string {
  if (!grade) return '';
  const ordered = [...gradeLevels].sort((a, b) => a.order - b.order);
  const index = ordered.findIndex((level) => level.description === grade);
  return index >= 0 ? ordered[index + 1]?.description ?? '' : '';
}

interface AddEnrollmentsWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  schoolId: string | undefined;
  schoolYearId: string | undefined;
  schoolYearName?: string;
  gradeLevels: GradeLevelItem[];
  /** With the checklist on, the wizard says what the checklist asks of each family. */
  checklistEnrollmentOn?: boolean;
}

function AddEnrollmentsWizard({
  open,
  onOpenChange,
  schoolId,
  schoolYearId,
  schoolYearName,
  gradeLevels,
  checklistEnrollmentOn = false,
}: AddEnrollmentsWizardProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [enrollmentKind, setEnrollmentKind] = useState('RE_ENROLLMENT');
  const [selectedFamilyIds, setSelectedFamilyIds] = useState<string[]>([]);
  const [studentSelections, setStudentSelections] = useState<StudentSelection[]>([]);

  const { data, loading } = useFamiliesForEnrollmentQuery(
    schoolId,
    schoolYearId,
    !open,
  );

  const [createEnrollments, { loading: creating }] = useCreateEnrollments();

  // Get families with their existing enrollment status
  const families = useMemo((): WizardFamilyWithStatus[] => {
    if (!data?.school?.families) return [];

    const existingFamilyEnrollmentIds = new Set(
      data.schoolYear?.familyEnrollments?.map((fe) => fe.family.id) || []
    );

    return data.school.families.map((family) => ({
      ...family,
      hasExistingEnrollment: existingFamilyEnrollmentIds.has(family.id),
    }));
  }, [data]);

  // Each child's grade in the year before this one, by person.
  const gradesBefore = useMemo(
    () => new Map((data?.schoolYear?.previousYear?.studentEnrollments ?? []).map((se) => [se.person.id, se.gradeLevel])),
    [data?.schoolYear?.previousYear]
  );

  // Get students who don't already have enrollments
  const existingStudentEnrollmentPersonIds = useMemo(() => {
    return new Set(
      data?.schoolYear?.studentEnrollments?.map((se) => se.person.id) || []
    );
  }, [data?.schoolYear?.studentEnrollments]);

  // Get children from selected families who don't have enrollments
  const availableStudents = useMemo(() => {
    const selectedFamilies = families.filter((f) => selectedFamilyIds.includes(f.id));
    const students: Array<{
      id: string;
      firstName: string | null;
      lastName: string | null;
      dateOfBirth: string | null;
      familyId: string;
      familyName: string;
    }> = [];

    selectedFamilies.forEach((family) => {
      (family.children || []).forEach((childMember) => {
        const person = childMember.person;
        if (person && !existingStudentEnrollmentPersonIds.has(person.id)) {
          students.push({
            id: person.id,
            firstName: person.firstName,
            lastName: person.lastName,
            dateOfBirth: person.dateOfBirth as string | null,
            familyId: family.id,
            familyName: family.name,
          });
        }
      });
    });

    return students;
  }, [families, selectedFamilyIds, existingStudentEnrollmentPersonIds]);

  const handleNext = () => {
    if (currentStep === 1) {
      // When moving from families to students, initialize student selections.
      // Each child starts on the grade after last year's; the office can change it.
      const initialSelections: StudentSelection[] = availableStudents.map((student) => ({
        personId: student.id,
        selected: true,
        gradeLevel: gradeAfter(gradesBefore.get(student.id), gradeLevels),
        enrollmentKind: null, // Will use family's kind by default
      }));
      setStudentSelections(initialSelections);
    }
    setCurrentStep(prev => Math.min(prev + 1, STEPS.length - 1));
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
  };

  const handleSubmit = async () => {
    const selectedStudents = studentSelections
      .filter(s => s.selected)
      .map(s => ({
        personId: s.personId,
        gradeLevel: s.gradeLevel || null,
        enrollmentKind: s.enrollmentKind || null,
      }));

    try {
      const result = await createEnrollments({
        variables: {
          input: {
            schoolYearId: schoolYearId!,
            enrollmentKind: enrollmentKind as "NEW_ADMISSION" | "RE_ENROLLMENT",
            familyIds: selectedFamilyIds,
            studentSelections: selectedStudents.map(s => ({
              ...s,
              enrollmentKind: s.enrollmentKind as "NEW_STUDENT" | "RETURNING_STUDENT" | null | undefined,
            })),
          },
        },
      });

      if ((result.data?.createEnrollments?.userErrors?.length ?? 0) > 0) {
        alert(result.data!.createEnrollments!.userErrors!.map((e) => e.message).join('\n'));
        return;
      }

      // Success - close wizard and reset
      handleClose();
    } catch (error: any) {
      console.error('Error creating enrollments:', error);
      alert('Error creating enrollments: ' + error.message);
    }
  };

  const handleClose = () => {
    setCurrentStep(0);
    setEnrollmentKind('RE_ENROLLMENT');
    setSelectedFamilyIds([]);
    setStudentSelections([]);
    onOpenChange(false);
  };

  const canProceed = () => {
    switch (STEPS[currentStep]) {
      case 'kind':
        return !!enrollmentKind;
      case 'families':
        return selectedFamilyIds.length > 0;
      case 'students':
        return studentSelections.some(s => s.selected);
      default:
        return true;
    }
  };

  const getStepTitle = () => {
    switch (STEPS[currentStep]) {
      case 'kind':
        return 'Select Enrollment Type';
      case 'families':
        return 'Select Families';
      case 'students':
        return 'Select Students';
      case 'confirm':
        return 'Confirm Enrollments';
      default:
        return 'Add Enrollments';
    }
  };

  const selectedStudentCount = studentSelections.filter(s => s.selected).length;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{getStepTitle()}</DialogTitle>
          <DialogDescription>
            Step {currentStep + 1} of {STEPS.length}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {loading ? (
            <div className="animate-pulse space-y-4">
              <div className="h-32 bg-muted rounded"></div>
            </div>
          ) : (
            <>
              {STEPS[currentStep] === 'kind' && (
                <SelectKindStep
                  value={enrollmentKind}
                  onChange={setEnrollmentKind}
                  checklistEnrollmentOn={checklistEnrollmentOn}
                />
              )}

              {STEPS[currentStep] === 'families' && (
                <SelectFamiliesStep
                  families={families}
                  selectedIds={selectedFamilyIds}
                  onSelectionChange={setSelectedFamilyIds}
                />
              )}

              {STEPS[currentStep] === 'students' && (
                <SelectStudentsStep
                  students={availableStudents}
                  selections={studentSelections}
                  onSelectionsChange={setStudentSelections}
                  gradeLevels={gradeLevels}
                  enrollmentKind={enrollmentKind}
                />
              )}

              {STEPS[currentStep] === 'confirm' && (
                <div className="space-y-4">
                  <div className="rounded-lg border p-4">
                    <h3 className="font-medium mb-2">Summary</h3>
                    <ul className="space-y-1 text-sm text-muted-foreground">
                      <li>
                        <strong>Enrollment Type:</strong>{' '}
                        {enrollmentKind === 'NEW_ADMISSION' ? 'New Admission' : 'Re-Enrollment'}
                      </li>
                      <li>
                        <strong>Families:</strong> {selectedFamilyIds.length}
                      </li>
                      <li>
                        <strong>Students:</strong> {selectedStudentCount}
                      </li>
                    </ul>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Click &quot;Create Enrollments&quot; to create the enrollment records.{' '}
                    {checklistEnrollmentOn
                      ? `Each family is added to ${schoolYearName ?? 'this year'} as Not invited. Invite them from the family's year when you're ready.`
                      : 'All enrollments will be created in DRAFT state.'}
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter className="flex justify-between">
          <div>
            {currentStep > 0 && (
              <Button variant="outline" onClick={handleBack} disabled={creating}>
                Back
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleClose} disabled={creating}>
              Cancel
            </Button>
            {currentStep < STEPS.length - 1 ? (
              <Button onClick={handleNext} disabled={!canProceed()}>
                Next
              </Button>
            ) : (
              <Button onClick={handleSubmit} disabled={creating || !canProceed()}>
                {creating ? 'Creating...' : 'Create Enrollments'}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default AddEnrollmentsWizard;
